import React, { useEffect } from 'react';
import { ArrowUpCircle, CloudOff, X } from 'lucide-react';
import { setOfflineState, useOfflineState } from '../store';
import { timeAgo } from '../format';
import { applyUpdate } from '../serviceWorkerRegistration';

/**
 * Two quiet, app-wide messages that must not live inside a page:
 *
 *  - "this is a stored copy, saved N ago" whenever the app served something
 *    from IndexedDB instead of the server. Stale data is never shown as live.
 *  - "a new version is ready" when a deploy is waiting to take over.
 *
 * Mounted from `index.tsx`, so it sits outside the router and is never
 * unmounted by a navigation.
 */
export const OfflineChrome: React.FC = () => {
  const { stale, online, updateReady } = useOfflineState();

  // A stored copy stops being news the moment real data can be fetched again.
  useEffect(() => {
    if (online && stale) setOfflineState({ stale: null });
  }, [online, stale]);

  if (!stale && !updateReady) return null;

  return (
    <div
      className="pointer-events-none fixed inset-x-0 z-50 flex flex-col items-center gap-2 px-3 font-ui"
      style={{ bottom: 'calc(var(--app-tabbar-h, 0px) + 12px)' }}
    >
      {stale && (
        <div className="pointer-events-auto flex w-full max-w-sm items-center gap-2.5 rounded-2xl border border-neutral-200 bg-white/95 px-3.5 py-2.5 backdrop-blur-xl">
          <CloudOff className="h-4 w-4 flex-shrink-0 text-neutral-400" strokeWidth={1.75} />
          <p className="min-w-0 flex-1 text-[12px] text-neutral-600">
            Showing a stored copy · saved {timeAgo(stale.cachedAt)}
          </p>
          <button
            type="button"
            aria-label="Dismiss"
            onClick={() => setOfflineState({ stale: null })}
            className="inline-flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full text-neutral-400 hover:bg-neutral-100 hover:text-ink"
          >
            <X className="h-3.5 w-3.5" strokeWidth={1.75} />
          </button>
        </div>
      )}

      {updateReady && (
        <div className="pointer-events-auto flex w-full max-w-sm items-center gap-2.5 rounded-2xl border border-neutral-200 bg-white/95 px-3.5 py-2.5 backdrop-blur-xl">
          <ArrowUpCircle className="h-4 w-4 flex-shrink-0 text-primary-600" strokeWidth={1.75} />
          <p className="min-w-0 flex-1 text-[12px] text-neutral-600">A new version is ready</p>
          <button
            type="button"
            onClick={() => void applyUpdate()}
            className="inline-flex h-7 flex-shrink-0 items-center rounded-full bg-primary-600 px-3 text-[12px] font-medium text-white transition-colors active:bg-primary-700"
          >
            Refresh
          </button>
        </div>
      )}
    </div>
  );
};
